/**
 * Quelltext-Leser für Mail-HTML (Zusammenarbeit, Änderungsvorschläge).
 *
 * Findet Elemente und Textstücke samt ihrer Stellen im Quelltext, ohne ihn
 * umzubauen. So ersetzt ein übernommener Vorschlag nur die geänderten
 * Textstellen; MSO-Kommentare, Attribut-Schreibweise und Leerzeilen bleiben
 * Zeichen für Zeichen, wie sie sind.
 *
 * Gezählt wird wie im Browser (DOMParser, iframe): Tag + Nummer unter allen
 * Elementen dieses Tags in Dokumentreihenfolge. Nachgebaut sind die Regeln,
 * die in Mails vorkommen: Kommentare samt MSO-Bedingungen
 * (`<!--[if mso]>…<![endif]-->`, `<!--[if !mso]><!-->`), Doctype (Quirks-Modus),
 * `<?…?>`, CDATA, Rohtext in script/style/textarea/title, Attribute in
 * Anführungszeichen (auch mit „>“), void-Elemente, fehlende End-Tags
 * (p, li, dt, dd, td, th, tr, option …), vom Parser ergänztes `<tbody>`/`<tr>`,
 * leeres `<p>` zu einem einzelnen `</p>`, wiederhergestellte und umgehängte
 * Formatierung (`<p><b>a<p>b`, `<b>a<p>b</b>c</p>`), Inhalt direkt in
 * `<table>`/`<tr>` (landet vor der Tabelle), Groß-/Kleinschreibung.
 *
 * html, head und body werden nicht gezählt: Sie sind nie Textblöcke, und der
 * Browser ergänzt sie nach Belieben. Ein ergänztes `<tbody>` wird mitgezählt
 * (ist aber nie ein Block). Damit ist die Nummer für alle Tags aus STRUKTUR
 * in DOM und Quelltext gleich.
 *
 * Geprüft gegen Chromium mit allen Mails aus kampagnen/, den Baukasten-Mails
 * und gut 20 000 zufälligen, oft kaputten Dokumenten.
 *
 * Nicht nachgebaut (in Mails nie): Sonderregeln in `<select>`, `<frameset>`,
 * die Verschachtelungsgrenze des Browsers (> 512 Ebenen) und Skripte (das
 * iframe läuft ohne allow-scripts, `<noscript>`-Inhalt ist darum normales
 * HTML). Weicht es doch ab, hilft beim Anwenden die Suche über den Text, und
 * die Prüfung nach jeder Änderung verhindert Schaden.
 */

// ── Gemeinsame Regeln (DOM und Quelltext) ───────────────────────────────────

/** Elemente, die einen Textblock gliedern: Ein Textblock enthält keins davon. */
export const STRUKTUR: ReadonlySet<string> = new Set([
  'table', 'tbody', 'thead', 'tfoot', 'tr', 'td', 'th', 'div', 'p',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'center', 'blockquote',
  'section', 'article', 'header', 'footer', 'main', 'nav', 'aside', 'figure',
  'figcaption', 'dl', 'dt', 'dd', 'pre', 'hr', 'form',
]);

/** Text in diesen Elementen ist kein Mailtext (Kopf, Stil, Skript, Formularfeld, Grafik). */
export const OHNE_TEXT: ReadonlySet<string> = new Set([
  'script', 'style', 'noscript', 'template', 'title', 'textarea', 'xmp', 'iframe',
  'noembed', 'noframes', 'plaintext', 'svg', 'math',
]);

/** Leerraum (auch geschützte Leerzeichen) zu einem Leerzeichen, außen getrimmt. */
export function normalisieren(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/** Läuft in Dokumentreihenfolge über die Textknoten, die als Mailtext zählen. */
export function domLaufen(wurzel: Node, text: (t: Text) => void, br?: () => void): void {
  for (let k = wurzel.firstChild; k; k = k.nextSibling) {
    if (k.nodeType === 3) text(k as Text);
    else if (k.nodeType === 1) {
      const name = (k as Element).localName;
      if (OHNE_TEXT.has(name)) continue;
      if (name === 'br') br?.();
      domLaufen(k, text, br);
    }
  }
}

/** Textknoten unter `wurzel` (ohne Text in OHNE_TEXT-Elementen). */
export function domTextKnoten(wurzel: Node): Text[] {
  const out: Text[] = [];
  domLaufen(wurzel, (t) => out.push(t));
  return out;
}

/** Text unter `wurzel`: Textknoten aneinander, `<br>` als Umbruch, normalisiert. */
export function domBlockText(wurzel: Node): string {
  let s = '';
  domLaufen(wurzel, (t) => { s += t.data; }, () => { s += '\n'; });
  return normalisieren(s);
}

// ── Entitäten ───────────────────────────────────────────────────────────────

let dekodierer: HTMLTextAreaElement | null = null;

const BENANNT: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', shy: '­',
  ndash: '–', mdash: '—', hellip: '…', euro: '€', copy: '©', reg: '®', middot: '·',
  auml: 'ä', ouml: 'ö', uuml: 'ü', Auml: 'Ä', Ouml: 'Ö', Uuml: 'Ü', szlig: 'ß',
  zwnj: '‌', zwj: '‍', bdquo: '„', ldquo: '“', rdquo: '”', lsquo: '‘', rsquo: '’',
};

/** Ohne Browser (Tests in Node): die häufigen Entitäten. */
function einfachDekodieren(s: string): string {
  return s.replace(/&(#[xX][0-9a-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]*);?/g, (m, n: string) => {
    if (n[0] === '#') {
      const c = n[1] === 'x' || n[1] === 'X' ? parseInt(n.slice(2), 16) : parseInt(n.slice(1), 10);
      return c > 0 && c <= 0x10ffff ? String.fromCodePoint(c) : '�';
    }
    return BENANNT[n] ?? m;
  });
}

function zeilenenden(s: string): string {
  return s.indexOf('\r') < 0 ? s : s.replace(/\r\n?/g, '\n');
}

/** Rohtext → Daten des Textknotens (Entitäten über ein `<textarea>`, CR/LF wie im Parser). */
export function dekodieren(roh: string): string {
  const s = roh.indexOf('\u0000') < 0 ? roh : roh.split('\u0000').join('');
  if (s.indexOf('&') < 0) return zeilenenden(s);
  if (typeof document === 'undefined') return einfachDekodieren(zeilenenden(s));
  dekodierer ??= document.createElement('textarea');
  dekodierer.innerHTML = s;
  return dekodierer.value;
}

// ── Datenform ───────────────────────────────────────────────────────────────

export type Namensraum = 'html' | 'svg' | 'math';

export interface QuellElement {
  /** Tag in Kleinbuchstaben (`<image>` zählt wie im Browser als img). */
  tag: string;
  ns: Namensraum;
  /** Index unter allen Elementen dieses Tags; -1 im Inhalt von `<template>`. */
  nr: number;
  /** Beginn des Start-Tags; bei ergänzten Elementen die Stelle, an der der Parser sie einsetzt. */
  start: number;
  /** Inhalt: von hinter dem Start-Tag … */
  innenStart: number;
  /** … bis vor den End-Tag bzw. bis zur Stelle, an der der Parser es schließt. */
  innenEnde: number;
  /** Hinter dem End-Tag (ohne eigenen End-Tag = innenEnde). */
  ende: number;
  endTag: boolean;
  /** Vom Parser ergänzt (tbody, tr, wiederhergestellte Formatierung, leeres p). */
  ergaenzt: boolean;
  /** Elternelement im DOM (Index in `elemente`), -1 = Dokument. */
  eltern: number;
  /** Selbst oder ein Vorfahr in OHNE_TEXT. */
  ohneText: boolean;
}

export interface QuellText {
  start: number;
  ende: number;
  /** Quelltextbereiche [start, ende, start, ende, …] — mehrere, wenn der Browser
   *  Stücke zu einem Knoten zusammenzieht (dazwischen ein ignorierter Tag). */
  teile: number[];
  /** Wie `Text.data` im DOM. */
  text: string;
  eltern: number;
  /** Zählt als Mailtext (nicht in OHNE_TEXT). */
  zaehlt: boolean;
}

export interface QuellKnoten { art: 'element' | 'text' | 'kommentar'; i: number }

export interface Quelltext {
  html: string;
  elemente: QuellElement[];
  texte: QuellText[];
  /** Kinder je Knoten in DOM-Reihenfolge: [0] = Dokument, [i + 1] = Element i. */
  kinder: QuellKnoten[][];
  /** Tag → Element-Indizes, Position = nr. */
  nachTag: Map<string, number[]>;
  quirks: boolean;
}

// ── Parser-Regeln ───────────────────────────────────────────────────────────

const RCDATA = new Set(['title', 'textarea']);
const P_SCHLIESSER = new Set(['address', 'article', 'aside', 'blockquote', 'center', 'details', 'dialog', 'dir', 'div', 'dl', 'fieldset', 'figcaption', 'figure', 'footer', 'header', 'hgroup', 'main', 'menu', 'nav', 'ol', 'p', 'search', 'section', 'summary', 'ul']);
const BLOCK_ENDE = new Set(['address', 'article', 'aside', 'blockquote', 'button', 'center', 'details', 'dialog', 'dir', 'div', 'dl', 'fieldset', 'figcaption', 'figure', 'footer', 'header', 'hgroup', 'listing', 'main', 'menu', 'nav', 'ol', 'pre', 'search', 'section', 'summary', 'ul', 'applet', 'marquee', 'object']);
const UEBERSCHRIFT = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
const FORMATIERUNG = new Set(['a', 'b', 'big', 'code', 'em', 'font', 'i', 'nobr', 's', 'small', 'strike', 'strong', 'tt', 'u']);
const IMPLIZIT = new Set(['dd', 'dt', 'li', 'optgroup', 'option', 'p', 'rb', 'rp', 'rt', 'rtc']);
/** Schließen die aktive Formatierung bis zur Marke — immer (Zelle, Beschriftung, Vorlage) … */
const MARKE = new Set(['td', 'th', 'caption', 'template']);
/** … bzw. nur mit dem eigenen End-Tag. */
const MARKE_EIGEN = new Set(['applet', 'marquee', 'object']);
const TABELLE_INNEN = new Set(['table', 'tbody', 'thead', 'tfoot', 'tr']);
const TABELLE_TEILE = new Set(['caption', 'colgroup', 'col', 'tbody', 'thead', 'tfoot', 'tr', 'td', 'th']);
const TABELLE_KONTEXT = new Set(['td', 'th', 'tr', 'tbody', 'thead', 'tfoot', 'table', 'caption', 'colgroup', 'template']);
const GRENZE = new Set(['applet', 'caption', 'html', 'table', 'td', 'th', 'marquee', 'object', 'template']);
const SPEZIELL = new Set([
  'address', 'applet', 'area', 'article', 'aside', 'base', 'basefont', 'bgsound', 'blockquote', 'body', 'br', 'button',
  'caption', 'center', 'col', 'colgroup', 'dd', 'details', 'dir', 'div', 'dl', 'dt', 'embed', 'fieldset', 'figcaption',
  'figure', 'footer', 'form', 'frame', 'frameset', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'head', 'header', 'hgroup', 'hr',
  'html', 'iframe', 'img', 'input', 'keygen', 'li', 'link', 'listing', 'main', 'marquee', 'menu', 'meta', 'nav',
  'noembed', 'noframes', 'noscript', 'object', 'ol', 'p', 'param', 'plaintext', 'pre', 'script', 'search', 'section',
  'select', 'source', 'style', 'summary', 'table', 'tbody', 'td', 'template', 'textarea', 'tfoot', 'th', 'thead',
  'title', 'tr', 'track', 'ul', 'wbr', 'xmp',
]);
/** Dürfen im Kopf stehen, bevor der Body beginnt. */
const KOPF = new Set(['html', 'head', 'base', 'basefont', 'bgsound', 'link', 'meta', 'noframes', 'script', 'style', 'template', 'title', 'noscript']);
const KOPF_NOSCRIPT = new Set(['basefont', 'bgsound', 'link', 'meta', 'noframes', 'style']);
const SVG_PUNKT = new Set(['foreignObject', 'desc', 'title']);
/** SVG-Tags in der Schreibweise des Browsers (localName). */
const SVG_NAMEN = new Map([
  'altGlyph', 'altGlyphDef', 'altGlyphItem', 'animateColor', 'animateMotion', 'animateTransform', 'clipPath',
  'feBlend', 'feColorMatrix', 'feComponentTransfer', 'feComposite', 'feConvolveMatrix', 'feDiffuseLighting',
  'feDisplacementMap', 'feDistantLight', 'feDropShadow', 'feFlood', 'feFuncA', 'feFuncB', 'feFuncG', 'feFuncR',
  'feGaussianBlur', 'feImage', 'feMerge', 'feMergeNode', 'feMorphology', 'feOffset', 'fePointLight',
  'feSpecularLighting', 'feSpotLight', 'feTile', 'feTurbulence', 'foreignObject', 'glyphRef', 'linearGradient',
  'radialGradient', 'textPath',
].map((n) => [n.toLowerCase(), n]));
const MATH_PUNKT = new Set(['mi', 'mo', 'mn', 'ms', 'mtext']);
const AUSBRUCH = new Set([
  'b', 'big', 'blockquote', 'body', 'br', 'center', 'code', 'dd', 'div', 'dl', 'dt', 'em', 'embed', 'h1', 'h2', 'h3',
  'h4', 'h5', 'h6', 'head', 'hr', 'i', 'img', 'li', 'listing', 'menu', 'meta', 'nobr', 'ol', 'p', 'pre', 'ruby', 's',
  'small', 'span', 'strong', 'strike', 'sub', 'sup', 'table', 'tt', 'u', 'ul', 'var',
]);

const QUIRKS_ANFANG = [
  '+//silmaril//', '-//as//', '-//advasoft ltd//', '-//ietf//dtd html', '-//metrius//', '-//microsoft//',
  '-//netscape comm. corp.//', "-//o'reilly and associates//", '-//softquad', '-//spyglass//', '-//sq//',
  '-//sun microsystems corp.//', '-//w3c//dtd html 3', '-//w3c//dtd html 4.0 frameset//',
  '-//w3c//dtd html 4.0 transitional//', '-//w3c//dtd html experimental', '-//w3c//dtd w3 html//',
  '-//w3o//dtd w3 html', '-//webtechs//dtd mozilla html',
];

/** Quirks-Modus wie im Browser: ohne `<!DOCTYPE html>` schließt `<table>` kein offenes `<p>`. */
function quirksFuer(inhalt: string): boolean {
  const name = /^\s*([^\s>]+)/.exec(inhalt)?.[1]?.toLowerCase();
  if (name !== 'html') return true;
  const pub = /PUBLIC\s*(?:"([^"]*)"|'([^']*)')/i.exec(inhalt);
  const p = pub ? (pub[1] ?? pub[2] ?? '').toLowerCase() : null;
  const sys = /(?:PUBLIC\s*(?:"[^"]*"|'[^']*')\s*|SYSTEM\s*)(?:"([^"]*)"|'([^']*)')/i.exec(inhalt);
  const s = sys ? (sys[1] ?? sys[2] ?? '').toLowerCase() : null;
  if (s === 'http://www.ibm.com/data/dtd/v11/ibmxhtml1-transitional.dtd') return true;
  if (p === null) return false;
  if (p === 'html' || p === '-//w3o//dtd w3 html strict 3.0//en//' || p === '-/w3c/dtd html 4.0 transitional/en') return true;
  if (QUIRKS_ANFANG.some((a) => p.startsWith(a))) return true;
  return s === null && (p.startsWith('-//w3c//dtd html 4.01 frameset//') || p.startsWith('-//w3c//dtd html 4.01 transitional//'));
}

function istLeerzeichen(c: number): boolean {
  return c === 9 || c === 10 || c === 12 || c === 13 || c === 32;
}

function istBuchstabe(c: number): boolean {
  return (c >= 65 && c <= 90) || (c >= 97 && c <= 122);
}

interface Tag {
  name: string;
  start: number;
  ende: number;
  selbst: boolean;
  attr: Record<string, string>;
}

/** Liest einen Start- oder End-Tag ab `<`. null = Dokument endet im Tag (der Browser verwirft ihn). */
function tagLesen(s: string, lt: number, endTag: boolean): Tag | null {
  const n = s.length;
  let j = lt + (endTag ? 2 : 1);
  const a = j;
  while (j < n) {
    const c = s.charCodeAt(j);
    if (istLeerzeichen(c) || c === 47 || c === 62) break;
    j++;
  }
  const name = s.slice(a, j).toLowerCase();
  const attr: Record<string, string> = {};
  for (;;) {
    while (j < n && istLeerzeichen(s.charCodeAt(j))) j++;
    if (j >= n) return null;
    const c = s.charCodeAt(j);
    if (c === 62) return { name, start: lt, ende: j + 1, selbst: false, attr };
    if (c === 47) {
      if (s.charCodeAt(j + 1) === 62) return { name, start: lt, ende: j + 2, selbst: true, attr };
      j++;
      continue;
    }
    // Attributname (das erste Zeichen darf auch „=“ sein)
    let k = j + 1;
    while (k < n) {
      const d = s.charCodeAt(k);
      if (istLeerzeichen(d) || d === 47 || d === 62 || d === 61) break;
      k++;
    }
    const aname = s.slice(j, k).toLowerCase();
    j = k;
    while (j < n && istLeerzeichen(s.charCodeAt(j))) j++;
    let wert = '';
    if (s.charCodeAt(j) === 61) {
      j++;
      while (j < n && istLeerzeichen(s.charCodeAt(j))) j++;
      const q = s.charCodeAt(j);
      if (q === 34 || q === 39) {
        const z = s.indexOf(q === 34 ? '"' : "'", j + 1);
        if (z < 0) return null;
        wert = s.slice(j + 1, z);
        j = z + 1;
      } else if (q !== 62) {
        k = j;
        while (k < n && !istLeerzeichen(s.charCodeAt(k)) && s.charCodeAt(k) !== 62) k++;
        wert = s.slice(j, k);
        j = k;
      }
    }
    if (!(aname in attr)) attr[aname] = wert;
  }
}

type Markup = 'start' | 'ende' | 'leer' | 'kommentar' | 'doctype' | 'cdata' | 'bogus';

function markupArt(s: string, lt: number): Markup | null {
  const c = s.charCodeAt(lt + 1);
  if (istBuchstabe(c)) return 'start';
  if (c === 47) {
    const d = s.charCodeAt(lt + 2);
    if (istBuchstabe(d)) return 'ende';
    if (d === 62) return 'leer';
    return Number.isNaN(d) ? null : 'bogus';
  }
  if (c === 33) {
    if (s.startsWith('--', lt + 2)) return 'kommentar';
    if (s.slice(lt + 2, lt + 9).toLowerCase() === 'doctype') return 'doctype';
    if (s.startsWith('[CDATA[', lt + 2)) return 'cdata';
    return 'bogus';
  }
  if (c === 63) return 'bogus';
  return null;
}

/** Ende eines Kommentars ab `<!--` (wie der Browser: `<!-->`, `<!--->`, `-->`, `--!>`). */
function kommentarEnde(s: string, lt: number): number {
  const i = lt + 4;
  if (s.charCodeAt(i) === 62) return i + 1;
  if (s.startsWith('->', i)) return i + 2;
  const a = s.indexOf('-->', i);
  const b = s.indexOf('--!>', i);
  if (a < 0 && b < 0) return s.length;
  if (b < 0 || (a >= 0 && a < b)) return a + 3;
  return b + 4;
}

function bisGroesser(s: string, ab: number): number {
  const k = s.indexOf('>', ab);
  return k < 0 ? s.length : k + 1;
}

class Leser {
  readonly s: string;
  readonly elemente: QuellElement[] = [];
  readonly texte: QuellText[] = [];
  readonly kinder: QuellKnoten[][] = [[]];
  readonly nachTag = new Map<string, number[]>();
  quirks = true;
  private readonly quirksFest: boolean | undefined;
  private stapel: number[] = [];
  private offen = new Set<number>();
  /** Aktive Formatierung (Element-Indizes), null = Marke (Zelle, Objekt, Vorlage). */
  private formatierung: (number | null)[] = [];
  /** Attribute je Formatierungselement (für die Dreier-Regel). */
  private formatAttr = new Map<number, string>();
  private template = 0;
  private formular = -1;
  private lfWeg = false;
  private begonnen = false;
  private doctype = false;
  /** Der Body hat begonnen (vorher ignoriert der Browser die meisten End-Tags). */
  private imBody = false;
  /** </head> war schon da: <noscript> gehört dann in den Body. */
  private kopfZu = false;
  /** Nach </body> bzw. </html>: Kommentare landen außerhalb des Body. */
  private nachBody = false;

  constructor(s: string, quirks?: boolean) {
    this.s = s;
    this.quirksFest = quirks;
    if (quirks !== undefined) this.quirks = quirks;
  }

  // ── Hilfen ──
  private el(k: number): QuellElement { return this.elemente[this.stapel[k]]; }
  private oben(): number { return this.stapel.length ? this.stapel[this.stapel.length - 1] : -1; }
  private obenTag(): string {
    const o = this.oben();
    return o >= 0 && this.elemente[o].ns === 'html' ? this.elemente[o].tag : '';
  }
  private kinderVon(i: number): QuellKnoten[] { return this.kinder[i + 1]; }
  private tabelleInnen(i: number): boolean {
    const e = this.elemente[i];
    return e.ns === 'html' && TABELLE_INNEN.has(e.tag);
  }
  private integrationspunkt(e: QuellElement): boolean {
    return (e.ns === 'svg' && SVG_PUNKT.has(e.tag)) || (e.ns === 'math' && MATH_PUNKT.has(e.tag));
  }
  /** Gelten für dieses Token die Regeln für SVG/MathML? */
  private fremd(art: 'start' | 'text' | 'ende'): boolean {
    const o = this.oben();
    if (o < 0) return false;
    const e = this.elemente[o];
    if (e.ns === 'html') return false;
    return art === 'ende' || !this.integrationspunkt(e);
  }
  private speziell(e: QuellElement): boolean {
    return e.ns === 'html' ? SPEZIELL.has(e.tag) : this.integrationspunkt(e) || (e.ns === 'math' && e.tag === 'annotation-xml');
  }
  private grenze(e: QuellElement, art: 'normal' | 'button' | 'liste' | 'tabelle'): boolean {
    if (art === 'tabelle') return e.ns === 'html' && (e.tag === 'table' || e.tag === 'template' || e.tag === 'html');
    if (e.ns !== 'html') return this.integrationspunkt(e) || (e.ns === 'math' && e.tag === 'annotation-xml');
    if (GRENZE.has(e.tag)) return true;
    if (art === 'button') return e.tag === 'button';
    if (art === 'liste') return e.tag === 'ol' || e.tag === 'ul';
    return false;
  }
  /** Stapel-Index des Elements `tag` im Bereich, sonst -1. */
  private imBereich(tag: string, art: 'normal' | 'button' | 'liste' | 'tabelle'): number {
    for (let k = this.stapel.length - 1; k >= 0; k--) {
      const e = this.el(k);
      if (e.ns === 'html' && e.tag === tag) return k;
      if (this.grenze(e, art)) return -1;
    }
    return -1;
  }
  private tabellenKontext(): number {
    for (let k = this.stapel.length - 1; k >= 0; k--) {
      const e = this.el(k);
      if (e.ns === 'html' && TABELLE_KONTEXT.has(e.tag)) return k;
    }
    return -1;
  }
  /** Inhalt direkt in einer Tabelle: vor die Tabelle (Element `vor`) — oder in eine jüngere <template>. */
  private fosterZiel(): { eltern: number; vor: number } | null {
    for (let k = this.stapel.length - 1; k >= 0; k--) {
      const e = this.el(k);
      if (e.ns !== 'html') continue;
      if (e.tag === 'template') return { eltern: this.stapel[k], vor: -1 };
      if (e.tag === 'table') return { eltern: e.eltern, vor: this.stapel[k] };
    }
    return null;
  }
  private vorIndex(eltern: number, vor: number): number {
    return vor < 0 ? -1 : this.kinderVon(eltern).findIndex((x) => x.art === 'element' && x.i === vor);
  }

  // ── Baum ──
  /** Element einsetzen (beim Elternelement oder — Inhalt direkt in einer Tabelle — vor der Tabelle). */
  private einfuegen(tag: string, ns: Namensraum, start: number, innenStart: number,
    opt: { ergaenzt?: boolean; leer?: boolean; direkt?: boolean } = {}): number {
    const i = this.elemente.length;
    const o = this.oben();
    let eltern = o;
    let vor = -1;
    if (!opt.direkt && o >= 0 && this.tabelleInnen(o)) {
      const z = this.fosterZiel();
      if (z) { eltern = z.eltern; vor = this.vorIndex(z.eltern, z.vor); }
    }
    const ohneText = this.template > 0 || OHNE_TEXT.has(tag) || (eltern >= 0 && this.elemente[eltern].ohneText);
    this.elemente.push({
      tag, ns, nr: -1, start, innenStart, innenEnde: innenStart, ende: innenStart, endTag: false,
      ergaenzt: !!opt.ergaenzt, eltern, ohneText,
    });
    this.kinder.push([]);
    const liste = this.kinderVon(eltern);
    const knoten: QuellKnoten = { art: 'element', i };
    if (vor >= 0) liste.splice(vor, 0, knoten); else liste.push(knoten);
    if (!opt.leer) { this.stapel.push(i); this.offen.add(i); }
    return i;
  }

  /** Hängt Element i um (ans Ende von `eltern` bzw. vor das Element `vor`). */
  private umhaengen(i: number, eltern: number, vor = -1): void {
    const e = this.elemente[i];
    if (e.eltern !== -2) {
      const alt = this.kinderVon(e.eltern);
      const p = alt.findIndex((x) => x.art === 'element' && x.i === i);
      if (p >= 0) alt.splice(p, 1);
    }
    e.eltern = eltern;
    const liste = this.kinderVon(eltern);
    const p = vor < 0 ? -1 : liste.findIndex((x) => x.art === 'element' && x.i === vor);
    if (p >= 0) liste.splice(p, 0, { art: 'element', i }); else liste.push({ art: 'element', i });
  }

  /** Noch nicht eingehängte Kopie eines Formatierungselements. */
  private klon(i: number, pos: number): number {
    const alt = this.elemente[i];
    const n = this.elemente.length;
    this.elemente.push({
      tag: alt.tag, ns: alt.ns, nr: -1, start: pos, innenStart: pos, innenEnde: pos, ende: pos, endTag: false,
      ergaenzt: true, eltern: -2, ohneText: alt.ohneText,
    });
    this.kinder.push([]);
    this.formatAttr.set(n, this.formatAttr.get(i) ?? '');
    return n;
  }

  private stapelEntfernen(i: number, pos: number, eigen: { ende: number } | null): void {
    const k = this.stapel.indexOf(i);
    if (k < 0) return;
    this.stapel.splice(k, 1);
    this.offen.delete(i);
    const e = this.elemente[i];
    e.innenEnde = pos;
    e.ende = eigen ? eigen.ende : pos;
    e.endTag = !!eigen;
  }

  /**
   * „Adoption agency“ wie im Browser: falsch verschachtelte Formatierung
   * (`<b>a<p>b</b>c</p>` → `<b>a</b><p><b>b</b>c</p>`) wird umgehängt und kopiert.
   * false: kein passendes Formatierungselement → wie ein sonstiger End-Tag.
   */
  private adoption(tag: string, pos: number, eigen: { ende: number } | null): boolean {
    const o = this.oben();
    if (o >= 0 && this.elemente[o].ns === 'html' && this.elemente[o].tag === tag && !this.formatierung.includes(o)) {
      this.abschliessen(this.stapel.length - 1, pos, eigen);
      return true;
    }
    for (let runde = 0; runde < 8; runde++) {
      const f = this.formatSuchen(tag);
      if (f < 0) return false;
      const fe = this.formatierung[f]!;
      if (!this.offen.has(fe)) { this.formatierung.splice(f, 1); return true; }
      const k = this.stapel.indexOf(fe);
      for (let j = this.stapel.length - 1; j > k; j--) if (this.grenze(this.el(j), 'normal')) return true;
      let fb = -1;
      for (let j = k + 1; j < this.stapel.length; j++) if (this.speziell(this.el(j))) { fb = j; break; }
      if (fb < 0) {
        this.abschliessen(k, pos, eigen);
        const g = this.formatierung.indexOf(fe);
        if (g >= 0) this.formatierung.splice(g, 1);
        return true;
      }
      const gemeinsam = k > 0 ? this.stapel[k - 1] : -1;
      const block = this.stapel[fb];
      let lesezeichen = f;
      let letzter = block;
      // wie Blink/WebKit: höchstens drei Schritte nach oben (ältere Fassung der Regel)
      let j = fb;
      for (let innen = 0; innen < 3; innen++) {
        j--;
        const n = this.stapel[j];
        const fi = this.formatierung.indexOf(n);
        if (fi < 0) { this.stapelEntfernen(n, pos, null); continue; }
        if (n === fe) break;
        const neu = this.klon(n, pos);
        this.formatierung[fi] = neu;
        this.stapel[j] = neu;
        this.offen.delete(n);
        this.offen.add(neu);
        const alt = this.elemente[n];
        alt.innenEnde = pos;
        alt.ende = pos;
        if (letzter === block) lesezeichen = fi + 1;
        this.umhaengen(letzter, neu);
        letzter = neu;
      }
      const z = gemeinsam >= 0 && this.tabelleInnen(gemeinsam) ? this.fosterZiel() : null;
      if (z) this.umhaengen(letzter, z.eltern, z.vor); else this.umhaengen(letzter, gemeinsam);
      // Inhalt des Blocks in eine Kopie der Formatierung
      const neuF = this.klon(fe, pos);
      const kinder = this.kinderVon(block).splice(0);
      this.kinder[neuF + 1] = kinder;
      for (const x of kinder) {
        if (x.art === 'element') this.elemente[x.i].eltern = neuF;
        else if (x.art === 'text') this.texte[x.i].eltern = neuF;
      }
      this.umhaengen(neuF, block);
      const ff = this.formatierung.indexOf(fe);
      this.formatierung.splice(ff, 1);
      if (ff < lesezeichen) lesezeichen--;
      this.formatierung.splice(lesezeichen, 0, neuF);
      this.stapelEntfernen(fe, pos, eigen);
      this.stapel.splice(this.stapel.indexOf(block) + 1, 0, neuF);
      this.offen.add(neuF);
    }
    return true;
  }

  private leer(tag: string, t: Tag, direkt = false): void {
    this.einfuegen(tag, 'html', t.start, t.ende, { leer: true, direkt });
  }

  /** Schließt alle Elemente ab Stapel-Index k (einschließlich). */
  private abschliessen(k: number, pos: number, eigen: { ende: number } | null): void {
    while (this.stapel.length > k) {
      const i = this.stapel.pop()!;
      const e = this.elemente[i];
      e.innenEnde = pos;
      if (eigen && this.stapel.length === k) { e.ende = eigen.ende; e.endTag = true; } else e.ende = pos;
      this.offen.delete(i);
      if (e.ns !== 'html') continue;
      if (MARKE.has(e.tag)) {
        while (this.formatierung.length) if (this.formatierung.pop() === null) break;
      }
      if (e.tag === 'template') this.template--;
    }
  }

  private implizitBis(pos: number, ausser?: string): void {
    for (;;) {
      const t = this.obenTag();
      if (!IMPLIZIT.has(t) || t === ausser) return;
      this.abschliessen(this.stapel.length - 1, pos, null);
    }
  }

  private pSchliessen(pos: number): void {
    const k = this.imBereich('p', 'button');
    if (k >= 0) this.abschliessen(k, pos, null);
  }

  private listenpunkt(tags: string[], pos: number): void {
    for (let k = this.stapel.length - 1; k >= 0; k--) {
      const e = this.el(k);
      if (e.ns === 'html' && tags.includes(e.tag)) { this.abschliessen(k, pos, null); return; }
      if (this.speziell(e) && !(e.ns === 'html' && (e.tag === 'address' || e.tag === 'div' || e.tag === 'p'))) return;
    }
  }

  private formatSuchen(tag: string): number {
    for (let f = this.formatierung.length - 1; f >= 0; f--) {
      const i = this.formatierung[f];
      if (i === null) return -1;
      if (this.elemente[i].tag === tag) return f;
    }
    return -1;
  }

  /** In die aktive Formatierung; höchstens drei gleiche (Tag + Attribute) seit der letzten Marke. */
  private formatMerken(i: number, attr: Record<string, string>): void {
    const sig = Object.keys(attr).sort().map((k) => `${k}=${attr[k]}`).join('\u0001');
    this.formatAttr.set(i, sig);
    const e = this.elemente[i];
    let gleiche = 0;
    let erster = -1;
    for (let f = this.formatierung.length - 1; f >= 0; f--) {
      const x = this.formatierung[f];
      if (x === null) break;
      const xe = this.elemente[x];
      if (xe.tag === e.tag && xe.ns === e.ns && this.formatAttr.get(x) === sig) { gleiche++; erster = f; }
    }
    if (gleiche >= 3) this.formatierung.splice(erster, 1);
    this.formatierung.push(i);
  }

  /** Formatierung, die ein Blockwechsel geschlossen hat, setzt der Browser neu an. */
  private wiederherstellen(pos: number): void {
    const f = this.formatierung;
    if (!f.length) return;
    const letzt = f[f.length - 1];
    if (letzt === null || this.offen.has(letzt)) return;
    let k = f.length - 1;
    while (k > 0) {
      const e = f[k - 1];
      if (e === null || this.offen.has(e)) break;
      k--;
    }
    for (; k < f.length; k++) {
      const alt = this.elemente[f[k]!];
      const neu = this.einfuegen(alt.tag, alt.ns, pos, pos, { ergaenzt: true });
      this.formatAttr.set(neu, this.formatAttr.get(f[k]!) ?? '');
      f[k] = neu;
    }
  }

  private textAnhaengen(eltern: number, vor: number, start: number, ende: number, text: string): void {
    const liste = this.kinderVon(eltern);
    const p = vor < 0 ? liste.length : vor;
    const davor = liste[p - 1];
    if (davor && davor.art === 'text') {
      const t = this.texte[davor.i];
      t.teile.push(start, ende);
      t.ende = ende;
      t.text += text;
      return;
    }
    const i = this.texte.length;
    this.texte.push({ start, ende, teile: [start, ende], text, eltern, zaehlt: eltern < 0 || !this.elemente[eltern].ohneText });
    liste.splice(p, 0, { art: 'text', i });
  }

  // ── Tokens ──
  private text(start: number, ende: number, art: 'daten' | 'rcdata' | 'roh' = 'daten'): void {
    if (this.lfWeg) {
      this.lfWeg = false;
      const c = this.s.charCodeAt(start);
      if (c === 10) start++;
      else if (c === 13) { start++; if (this.s.charCodeAt(start) === 10) start++; }
    }
    if (start >= ende) return;
    const roh = this.s.slice(start, ende);
    if (art === 'roh') {
      this.textAnhaengen(this.oben(), -1, start, ende, zeilenenden(roh).split('\u0000').join('�'));
      return;
    }
    const text = dekodieren(roh);
    if (!text) return;
    if (art === 'rcdata') { this.textAnhaengen(this.oben(), -1, start, ende, text); return; }
    const leer = /^[\t\n\f\r ]*$/.test(text);
    if (!leer) { this.begonnen = true; this.nachBody = false; this.kopfVerlassen(start); }
    if (this.fremd('text')) { this.textAnhaengen(this.oben(), -1, start, ende, text); return; }
    if (this.obenTag() === 'colgroup' && !leer) this.abschliessen(this.stapel.length - 1, start, null);
    const o = this.oben();
    if (o >= 0 && leer && (this.tabelleInnen(o) || this.obenTag() === 'colgroup')) { this.textAnhaengen(o, -1, start, ende, text); return; }
    this.wiederherstellen(start);
    const o2 = this.oben();
    const z = o2 >= 0 && this.tabelleInnen(o2) ? this.fosterZiel() : null;
    // Text direkt in einer Tabelle: der Browser setzt ihn vor die Tabelle
    if (z) this.textAnhaengen(z.eltern, this.vorIndex(z.eltern, z.vor), start, ende, text);
    else this.textAnhaengen(o2, -1, start, ende, text);
  }

  /** Inhalt vor dem Body beendet den Kopf; ein <noscript> im Kopf schließt dabei. */
  private kopfVerlassen(pos: number): void {
    if (this.imBody) return;
    this.imBody = true;
    if (this.obenTag() === 'noscript') this.abschliessen(this.stapel.length - 1, pos, null);
  }

  private kommentar(): void {
    this.lfWeg = false;
    this.kinderVon(this.nachBody ? -1 : this.oben()).push({ art: 'kommentar', i: -1 });
  }

  lesen(): void {
    const s = this.s;
    const n = s.length;
    let i = 0;
    let textAb = 0;
    while (i < n) {
      const lt = s.indexOf('<', i);
      if (lt < 0) break;
      const art = markupArt(s, lt);
      if (!art) { i = lt + 1; continue; }
      if (lt > textAb) this.text(textAb, lt);
      i = textAb = this.markup(art, lt);
    }
    if (textAb < n) this.text(textAb, n);
    this.abschliessen(0, n, null);
    this.nummerieren();
  }

  /** nr in Dokumentreihenfolge (wie getElementsByTagName), ohne Inhalt von <template>. */
  private nummerieren(): void {
    const laufen = (i: number) => {
      for (const k of this.kinder[i + 1]) {
        if (k.art !== 'element') continue;
        const e = this.elemente[k.i];
        let liste = this.nachTag.get(e.tag);
        if (!liste) { liste = []; this.nachTag.set(e.tag, liste); }
        e.nr = liste.length;
        liste.push(k.i);
        if (!(e.tag === 'template' && e.ns === 'html')) laufen(k.i);
      }
    };
    laufen(-1);
  }

  private markup(art: Markup, lt: number): number {
    const s = this.s;
    switch (art) {
      case 'kommentar': this.kommentar(); return kommentarEnde(s, lt);
      case 'bogus': this.kommentar(); return bisGroesser(s, lt + 2);
      case 'leer': return lt + 3;
      case 'doctype': {
        const e = bisGroesser(s, lt + 9);
        this.lfWeg = false;
        if (!this.begonnen && !this.doctype) {
          this.doctype = true;
          if (this.quirksFest === undefined) this.quirks = quirksFuer(s.slice(lt + 9, s[e - 1] === '>' ? e - 1 : e));
        }
        return e;
      }
      case 'cdata': {
        if (!this.fremd('text')) { this.kommentar(); return bisGroesser(s, lt + 2); }
        this.lfWeg = false;
        const z = s.indexOf(']]>', lt + 9);
        const e = z < 0 ? s.length : z;
        if (e > lt + 9) this.textAnhaengen(this.oben(), -1, lt + 9, e, zeilenenden(s.slice(lt + 9, e)));
        return z < 0 ? s.length : z + 3;
      }
      case 'start': {
        this.lfWeg = false;
        const t = tagLesen(s, lt, false);
        if (!t) return s.length;
        this.begonnen = true;
        const i = this.start(t);
        if (i < 0) return t.ende;
        const e = this.elemente[i];
        if (e.tag === 'plaintext') { this.text(t.ende, s.length, 'roh'); return s.length; }
        return this.rohtext(i, t.ende, RCDATA.has(e.tag) ? 'rcdata' : 'roh');
      }
      case 'ende': {
        this.lfWeg = false;
        const t = tagLesen(s, lt, true);
        if (!t) return s.length;
        this.ende(t.name, lt, t.ende);
        return t.ende;
      }
    }
  }

  /** Inhalt von script/style/textarea/title …: bis zum passenden End-Tag nur Text. */
  private rohtext(i: number, ab: number, art: 'rcdata' | 'roh'): number {
    const s = this.s;
    const re = new RegExp(`</${this.elemente[i].tag}[\\t\\n\\f\\r />]`, 'ig');
    re.lastIndex = ab;
    const m = re.exec(s);
    const z = m ? m.index : s.length;
    this.text(ab, z, art);
    this.lfWeg = false;
    if (!m) return s.length;
    const t = tagLesen(s, z, true);
    if (!t) return s.length;
    this.ende(t.name, z, t.ende);
    return t.ende;
  }

  /** Start-Tag verarbeiten. Rückgabe: Element, dessen Inhalt Rohtext ist, sonst -1. */
  private start(t: Tag): number {
    let tag = t.name;
    const pos = t.start;
    const ende = t.ende;
    if (this.fremd('start')) {
      const ausbruch = AUSBRUCH.has(tag) || (tag === 'font' && ('color' in t.attr || 'face' in t.attr || 'size' in t.attr));
      if (!ausbruch) {
        const ns = this.elemente[this.oben()].ns;
        const i = this.einfuegen(ns === 'svg' ? SVG_NAMEN.get(tag) ?? tag : tag, ns, pos, ende, { direkt: true });
        if (t.selbst) this.abschliessen(this.stapel.lastIndexOf(i), ende, { ende });
        return -1;
      }
      while (this.stapel.length && this.fremd('start')) this.abschliessen(this.stapel.length - 1, pos, null);
    }
    if (tag === 'image') tag = 'img';
    if (tag !== 'html') this.nachBody = false;
    if (!this.imBody) {
      if (this.obenTag() === 'noscript' && !KOPF_NOSCRIPT.has(tag)) this.abschliessen(this.stapel.length - 1, pos, null);
      if (!KOPF.has(tag) || (this.kopfZu && tag === 'noscript')) this.kopfVerlassen(pos);
    }
    if (this.obenTag() === 'colgroup' && tag !== 'col' && tag !== 'template') this.abschliessen(this.stapel.length - 1, pos, null);

    switch (tag) {
      case 'html': case 'head': case 'body': case 'frameset': case 'frame':
        return -1;
      case 'base': case 'basefont': case 'bgsound': case 'link': case 'meta':
        this.leer(tag, t, true);
        return -1;
      case 'title': case 'style': case 'script': case 'noframes':
        return this.einfuegen(tag, 'html', pos, ende, { direkt: true });
      case 'template':
        this.einfuegen(tag, 'html', pos, ende, { direkt: true });
        this.formatierung.push(null);
        this.template++;
        return -1;
    }
    if (TABELLE_TEILE.has(tag)) { this.tabellenStart(tag, t); return -1; }

    // Tabellen-Modus (auch wenn oben ein vor die Tabelle gesetztes Element liegt)
    const tk = this.tabellenKontext();
    const inTabelle = tk >= 0 && TABELLE_INNEN.has(this.el(tk).tag);
    if (tag === 'table') {
      const k = this.tabellenKontext();
      if (k >= 0 && !['td', 'th', 'caption', 'template'].includes(this.el(k).tag)) {
        // Tabelle direkt in einer Tabelle: der Browser schließt die äußere
        const tk = this.imBereich('table', 'tabelle');
        if (tk < 0) return -1;
        this.abschliessen(tk, pos, null);
      }
      if (!this.quirks) this.pSchliessen(pos);
      this.einfuegen(tag, 'html', pos, ende);
      return -1;
    }
    if (inTabelle && tag === 'input' && (t.attr.type ?? '').toLowerCase() === 'hidden') { this.leer(tag, t, true); return -1; }
    if (inTabelle && tag === 'form') {
      if (this.formular >= 0 || this.template > 0) return -1;
      this.formular = this.einfuegen(tag, 'html', pos, ende, { direkt: true });
      this.abschliessen(this.stapel.length - 1, ende, null);
      return -1;
    }
    if (P_SCHLIESSER.has(tag)) { this.pSchliessen(pos); this.einfuegen(tag, 'html', pos, ende); return -1; }
    if (UEBERSCHRIFT.has(tag)) {
      this.pSchliessen(pos);
      if (UEBERSCHRIFT.has(this.obenTag())) this.abschliessen(this.stapel.length - 1, pos, null);
      this.einfuegen(tag, 'html', pos, ende);
      return -1;
    }
    switch (tag) {
      case 'pre': case 'listing':
        this.pSchliessen(pos);
        this.einfuegen(tag, 'html', pos, ende);
        this.lfWeg = true;
        return -1;
      case 'form':
        if (this.formular >= 0 && this.template === 0) return -1;
        this.pSchliessen(pos);
        {
          const i = this.einfuegen(tag, 'html', pos, ende);
          if (this.template === 0) this.formular = i;
        }
        return -1;
      case 'li':
        this.listenpunkt(['li'], pos);
        this.pSchliessen(pos);
        this.einfuegen(tag, 'html', pos, ende);
        return -1;
      case 'dd': case 'dt':
        this.listenpunkt(['dd', 'dt'], pos);
        this.pSchliessen(pos);
        this.einfuegen(tag, 'html', pos, ende);
        return -1;
      case 'plaintext':
        this.pSchliessen(pos);
        return this.einfuegen(tag, 'html', pos, ende);
      case 'button': {
        const k = this.imBereich('button', 'normal');
        if (k >= 0) this.abschliessen(k, pos, null);
        this.wiederherstellen(pos);
        this.einfuegen(tag, 'html', pos, ende);
        return -1;
      }
      case 'a': {
        const f = this.formatSuchen('a');
        if (f >= 0) {
          // <a> in <a>: der Browser schließt das äußere
          const a = this.formatierung[f]!;
          this.adoption('a', pos, null);
          const g = this.formatierung.indexOf(a);
          if (g >= 0) this.formatierung.splice(g, 1);
          this.stapelEntfernen(a, pos, null);
        }
        this.wiederherstellen(pos);
        this.formatMerken(this.einfuegen(tag, 'html', pos, ende), t.attr);
        return -1;
      }
      case 'applet': case 'marquee': case 'object':
        this.wiederherstellen(pos);
        this.einfuegen(tag, 'html', pos, ende);
        this.formatierung.push(null);
        return -1;
      case 'area': case 'br': case 'embed': case 'img': case 'keygen': case 'wbr': case 'input':
        this.wiederherstellen(pos);
        this.leer(tag, t);
        return -1;
      case 'param': case 'source': case 'track':
        this.leer(tag, t);
        return -1;
      case 'hr':
        this.pSchliessen(pos);
        this.leer(tag, t);
        return -1;
      case 'textarea': {
        const i = this.einfuegen(tag, 'html', pos, ende);
        this.lfWeg = true;
        return i;
      }
      case 'xmp':
        this.pSchliessen(pos);
        this.wiederherstellen(pos);
        return this.einfuegen(tag, 'html', pos, ende);
      case 'iframe': case 'noembed':
        return this.einfuegen(tag, 'html', pos, ende);
      case 'optgroup': case 'option':
        if (this.obenTag() === 'option') this.abschliessen(this.stapel.length - 1, pos, null);
        this.wiederherstellen(pos);
        this.einfuegen(tag, 'html', pos, ende);
        return -1;
      case 'rb': case 'rtc': case 'rp': case 'rt':
        if (this.imBereich('ruby', 'normal') >= 0) this.implizitBis(pos, tag === 'rp' || tag === 'rt' ? 'rtc' : undefined);
        this.einfuegen(tag, 'html', pos, ende);
        return -1;
      case 'math': case 'svg': {
        this.wiederherstellen(pos);
        const i = this.einfuegen(tag, tag, pos, ende);
        if (t.selbst) this.abschliessen(this.stapel.lastIndexOf(i), ende, { ende });
        return -1;
      }
    }
    if (FORMATIERUNG.has(tag)) {
      this.wiederherstellen(pos);
      if (tag === 'nobr' && this.imBereich('nobr', 'normal') >= 0) {
        this.adoption('nobr', pos, null);
        this.wiederherstellen(pos);
      }
      this.formatMerken(this.einfuegen(tag, 'html', pos, ende), t.attr);
      return -1;
    }
    this.wiederherstellen(pos);
    this.einfuegen(tag, 'html', pos, ende);
    return -1;
  }

  /** td, tr, tbody … : offene Zellen/Zeilen schließen, fehlendes tbody/tr ergänzen. */
  private tabellenStart(tag: string, t: Tag): void {
    const pos = t.start;
    let k = this.tabellenKontext();
    if (k < 0) return; // außerhalb einer Tabelle ignoriert der Browser den Tag
    let kt = this.el(k).tag;
    if (kt === 'template') {
      if (tag === 'col') this.leer(tag, t, true);
      else {
        this.einfuegen(tag, 'html', pos, t.ende, { direkt: true });
        if (tag === 'td' || tag === 'th' || tag === 'caption') this.formatierung.push(null);
      }
      return;
    }
    if (tag === 'col' && kt === 'colgroup') { this.leer(tag, t, true); return; }
    if (kt === 'td' || kt === 'th' || kt === 'caption' || kt === 'colgroup') {
      this.abschliessen(k, pos, null);
      k = this.tabellenKontext();
      if (k < 0) return;
      kt = this.el(k).tag;
    }
    if (tag === 'td' || tag === 'th') {
      if (kt === 'table') {
        this.abschliessen(k + 1, pos, null);
        this.einfuegen('tbody', 'html', pos, pos, { ergaenzt: true, direkt: true });
        k = this.stapel.length - 1;
        kt = 'tbody';
      }
      if (kt === 'tbody' || kt === 'thead' || kt === 'tfoot') {
        this.abschliessen(k + 1, pos, null);
        this.einfuegen('tr', 'html', pos, pos, { ergaenzt: true, direkt: true });
        k = this.stapel.length - 1;
      }
      this.abschliessen(k + 1, pos, null);
      this.einfuegen(tag, 'html', pos, t.ende, { direkt: true });
      this.formatierung.push(null);
      return;
    }
    if (tag === 'tr') {
      if (kt === 'tr') {
        this.abschliessen(k, pos, null);
        k = this.tabellenKontext();
        if (k < 0) return;
        kt = this.el(k).tag;
      }
      if (kt === 'table') {
        this.abschliessen(k + 1, pos, null);
        this.einfuegen('tbody', 'html', pos, pos, { ergaenzt: true, direkt: true });
        k = this.stapel.length - 1;
      }
      this.abschliessen(k + 1, pos, null);
      this.einfuegen(tag, 'html', pos, t.ende, { direkt: true });
      return;
    }
    // tbody, thead, tfoot, caption, colgroup, col: zurück bis zur Tabelle
    const tk = this.imBereich('table', 'tabelle');
    if (tk < 0) return;
    this.abschliessen(tk + 1, pos, null);
    if (tag === 'col') {
      this.einfuegen('colgroup', 'html', pos, pos, { ergaenzt: true, direkt: true });
      this.leer(tag, t, true);
      return;
    }
    this.einfuegen(tag, 'html', pos, t.ende, { direkt: true });
    if (tag === 'caption') this.formatierung.push(null);
  }

  private ende(tag: string, pos: number, endePos: number): void {
    const eigen = { ende: endePos };
    if (!this.imBody) {
      // im Kopf zählen nur </br>, </body>, </html>, </template> und das Ende des offenen Elements (title, style, noscript …)
      if (tag === 'head') { this.kopfZu = true; return; }
      if (tag === 'br' || tag === 'body' || tag === 'html') this.kopfVerlassen(pos);
      else if (tag !== 'template' && this.obenTag() !== tag) return;
    }
    if (tag === 'body' || tag === 'html') {
      // </body> zählt nur, wenn der Body „im Bereich“ ist (nicht in Zelle, Tabelle, Objekt …)
      if (!this.stapel.some((i) => this.grenze(this.elemente[i], 'normal'))) this.nachBody = true;
      return;
    }
    this.nachBody = false;
    if (this.fremd('ende')) {
      if (tag === 'br' || tag === 'p') {
        while (this.stapel.length && this.fremd('start')) this.abschliessen(this.stapel.length - 1, pos, null);
      } else {
        for (let k = this.stapel.length - 1; k >= 0; k--) {
          const e = this.el(k);
          if (e.ns === 'html') break;
          if (e.tag.toLowerCase() === tag) { this.abschliessen(k, pos, eigen); return; }
        }
      }
    }
    switch (tag) {
      case 'html': case 'body': case 'head': case 'col':
        return;
      case 'template': {
        for (let k = this.stapel.length - 1; k >= 0; k--) {
          if (this.el(k).ns === 'html' && this.el(k).tag === 'template') { this.abschliessen(k, pos, eigen); return; }
        }
        return;
      }
      case 'p': {
        const k = this.imBereich('p', 'button');
        if (k >= 0) { this.abschliessen(k, pos, eigen); return; }
        // einzelnes </p>: der Browser setzt ein leeres <p> ein
        this.einfuegen('p', 'html', pos, pos, { ergaenzt: true });
        this.abschliessen(this.stapel.length - 1, pos, null);
        return;
      }
      case 'li': {
        const k = this.imBereich('li', 'liste');
        if (k >= 0) this.abschliessen(k, pos, eigen);
        return;
      }
      case 'dd': case 'dt': {
        const k = this.imBereich(tag, 'normal');
        if (k >= 0) this.abschliessen(k, pos, eigen);
        return;
      }
      case 'form': {
        const f = this.formular;
        if (this.template > 0) {
          const k = this.imBereich('form', 'normal');
          if (k >= 0) this.abschliessen(k, pos, eigen);
          return;
        }
        this.formular = -1;
        if (f < 0 || !this.offen.has(f)) return;
        const k = this.stapel.lastIndexOf(f);
        for (let j = this.stapel.length - 1; j > k; j--) if (this.grenze(this.el(j), 'normal')) return;
        // nur das Formular verlässt den Stapel, sein Inhalt bleibt offen
        const e = this.elemente[f];
        e.innenEnde = pos;
        e.ende = endePos;
        e.endTag = true;
        this.stapel.splice(k, 1);
        this.offen.delete(f);
        return;
      }
      case 'br': {
        this.wiederherstellen(pos);
        this.einfuegen('br', 'html', pos, endePos, { leer: true });
        return;
      }
      case 'td': case 'th': case 'tr': case 'tbody': case 'thead': case 'tfoot': case 'table': case 'caption': {
        const k = this.imBereich(tag, 'tabelle');
        if (k >= 0) this.abschliessen(k, pos, eigen);
        return;
      }
      case 'colgroup':
        if (this.obenTag() === 'colgroup') this.abschliessen(this.stapel.length - 1, pos, eigen);
        return;
    }
    if (UEBERSCHRIFT.has(tag)) {
      for (let k = this.stapel.length - 1; k >= 0; k--) {
        const e = this.el(k);
        if (e.ns === 'html' && UEBERSCHRIFT.has(e.tag)) { this.abschliessen(k, pos, eigen); return; }
        if (this.grenze(e, 'normal')) return;
      }
      return;
    }
    if (BLOCK_ENDE.has(tag)) {
      const k = this.imBereich(tag, 'normal');
      if (k < 0) return;
      this.abschliessen(k, pos, eigen);
      if (MARKE_EIGEN.has(tag)) while (this.formatierung.length) if (this.formatierung.pop() === null) break;
      return;
    }
    if (FORMATIERUNG.has(tag) && this.adoption(tag, pos, eigen)) return;
    // sonstige End-Tags
    for (let k = this.stapel.length - 1; k >= 0; k--) {
      const e = this.el(k);
      if (e.ns === 'html' && e.tag === tag) { this.abschliessen(k, pos, eigen); return; }
      if (this.speziell(e)) return;
    }
  }
}

/**
 * Liest Mail-HTML wie der Browser, ohne es zu verändern.
 * `quirks` erzwingt den Modus (sonst aus dem Doctype, wie DOMParser).
 */
export function quelltextLesen(html: string, optionen: { quirks?: boolean } = {}): Quelltext {
  const l = new Leser(html, optionen.quirks);
  l.lesen();
  return { html, elemente: l.elemente, texte: l.texte, kinder: l.kinder, nachTag: l.nachTag, quirks: l.quirks };
}

// ── Textblöcke im Quelltext (Gegenstück zu textbloecke() im DOM) ─────────────

function quellLaufen(q: Quelltext, i: number, text: (t: number) => void, br?: () => void): void {
  if (i >= 0 && q.elemente[i].tag === 'template' && q.elemente[i].ns === 'html') return; // Inhalt liegt im DOM nicht unter dem Element
  for (const k of q.kinder[i + 1]) {
    if (k.art === 'text') text(k.i);
    else if (k.art === 'element') {
      const tag = q.elemente[k.i].tag;
      if (OHNE_TEXT.has(tag)) continue;
      if (tag === 'br') br?.();
      quellLaufen(q, k.i, text, br);
    }
  }
}

/** Texte (Indizes in `q.texte`) unter Element i in Dokumentreihenfolge. */
export function quellTextKnoten(q: Quelltext, i: number): number[] {
  const out: number[] = [];
  quellLaufen(q, i, (t) => out.push(t));
  return out;
}

/**
 * Normalisierter Text unter Element i — wie domBlockText() im DOM. Mit
 * `ersatz` stehen diese Texte an Stelle der Textstücke (Reihenfolge wie
 * quellTextKnoten), `<br>` zählt weiter als Umbruch.
 */
export function quellBlockText(q: Quelltext, i: number, ersatz?: readonly string[]): string {
  let s = '';
  let k = 0;
  quellLaufen(q, i, (t) => { s += ersatz ? (ersatz[k++] ?? '') : q.texte[t].text; }, () => { s += '\n'; });
  return normalisieren(s);
}

/** Bits je Element: 1 = enthält Mailtext, 2 = enthält ein STRUKTUR-Element. */
export function quellBits(q: Quelltext): Uint8Array {
  const bits = new Uint8Array(q.elemente.length);
  const pruefen = (i: number): number => {
    let b = 0;
    for (const k of q.kinder[i + 1]) {
      if (k.art === 'text') {
        if (/\S/.test(q.texte[k.i].text)) b |= 1;
      } else if (k.art === 'element') {
        const tag = q.elemente[k.i].tag;
        if (OHNE_TEXT.has(tag)) continue;
        b |= pruefen(k.i);
        if (STRUKTUR.has(tag)) b |= 2;
      }
    }
    if (i >= 0) bits[i] = b;
    return b;
  };
  pruefen(-1);
  return bits;
}

/** Textblöcke (Element-Indizes) in Dokumentreihenfolge — dieselben Regeln wie textbloecke(). */
export function quellBloecke(q: Quelltext): number[] {
  const bits = quellBits(q);
  const out: number[] = [];
  const sammeln = (i: number) => {
    for (const k of q.kinder[i + 1]) {
      if (k.art !== 'element') continue;
      const e = q.elemente[k.i];
      if (OHNE_TEXT.has(e.tag)) continue;
      if (bits[k.i] === 1 && e.nr >= 0) out.push(k.i);
      else sammeln(k.i);
    }
  };
  sammeln(-1);
  return out;
}
